import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EndPointApi } from '@helpers/endpointapi';
import { Subject, lastValueFrom } from 'rxjs';
import {
    generateInnerTable,
    getDeletedChannels,
    getFooter,
    getGroupHeaders,
    getHeader,
    getNewChannels,
    getPdfLayout,
    isEnableToggled,
    PAGE_SIZE,
    pagesPerChannelGroup,
    prepareChannelDifferences,
    prepareGroupDiffrences,
} from './print-channels-utils';
import { ChannelGroup } from 'icreate/common/models/channel-group';
import { Channel } from 'icreate/common/models/channel';
import {
    ChannelType,
    ChannelTypeStrings,
    isAnalog,
    isDigitalMotor,
    isExhaustGas,
    isExhGasDev,
    isFlowCounter,
    isRunningHour,
} from 'icreate/common/models/channel-type';
import { ChannelService } from '@service/channel/channel.service';
import { ChannelCompareService } from '@service/channel/channel-compare.service';
import { VesselService } from '@service/vessel.service';
import { isBoolean } from 'lodash';
import { AlarmLevel } from 'icreate/common/models/alarm-level';
import { TranslateService } from '@ngx-translate/core';
import { getSimpleType, getSpecialType } from 'icreate/common/services/channel-object-utils';
import { PrintDialogComponent, PrintPageType, PrintType } from '@components/print-dialog/print-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ChannelGroupService } from '@service/channel/channel-group.service';
import { LoadingDialogComponent } from '@layout/loading-dialog/loading-dialog.component';

export class Group {
    name: string;
    checked: boolean;
    id: string;
}

@Injectable({ providedIn: 'root' })
export class PrintChannelsService {
    private domain: string;
    private DEFAULT_MARGIN = [1, 0, 1, 0];

    constructor(
        private http: HttpClient,
        private channelService: ChannelService,
        private channelCompareService: ChannelCompareService,
        private vesselService: VesselService,
        private translate: TranslateService,
        private channelGroupService: ChannelGroupService,
        private dialog: MatDialog
    ) {
        this.domain = EndPointApi.iApiDomain();
    }

    async triggerPrint(vessel, comp: boolean = false): Promise<boolean> {
        let loadingRef = null;

        try {
            const channelGroupList = await this.channelGroupService.getList(vessel.id);
            const dialogRef = this.dialog.open(PrintDialogComponent, {
                data: {
                    pageType: PrintPageType.CHANNEL_SHEET,
                    vessel: vessel,
                    iAPIVesselId: vessel.iAPIVesselId,
                    shipyardId: vessel.shipyardId,
                    options: channelGroupList,
                    notMain: comp,
                },
            });

            const shouldPrint = await lastValueFrom(dialogRef.afterClosed());
            if (!shouldPrint) return true;
            if (shouldPrint.cancel) return false;
            const { printType, version, iAPIShipyardlId, setListIds } = shouldPrint;

            if (!printType) {
                return true;
            }

            loadingRef = this.dialog.open(LoadingDialogComponent, {
                width: '320px',
                disableClose: true,
                data: { progress: 10 },
            });

            const vesselId = vessel.id;
            const groupsToPrint = channelGroupList.filter(group => setListIds.has(group.id));

            let oldVessel = undefined;
            let versionsDifferences = undefined;

            if (printType === PrintType.UPDATES) {
                if (!iAPIShipyardlId || !version) {
                    loadingRef.close();
                    return false;
                }
                versionsDifferences = await this.getChannelsDifference(
                    vesselId,
                    vessel.iAPIVesselId,
                    iAPIShipyardlId,
                    version
                );
                oldVessel = versionsDifferences?.oldVessel;
            }

            loadingRef.componentInstance.data.progress = 40;

            const [pdfMake, pdfFonts, currentChannels] = await Promise.all([
                import('pdfmake/build/pdfmake'),
                import('pdfmake/build/vfs_fonts'),
                this.getCurrentChannels(vesselId, groupsToPrint),
            ]);

            loadingRef.componentInstance.data.progress = 75;

            const pdfData = this.getPdfFile(currentChannels, versionsDifferences, groupsToPrint, oldVessel);

            const runtimeVfs = {
                ...pdfFonts.default?.pdfMake?.vfs,
            };

            const baseHref = window.location.origin + '/';

            const fonts = {
                MSGothic: {
                    normal: baseHref + 'assets/fonts/MSGothic.ttf',
                    bold: baseHref + 'assets/fonts/MSGothic.ttf',
                    italics: baseHref + 'assets/fonts/MSGothic.ttf',
                    bolditalics: baseHref + 'assets/fonts/MSGothic.ttf',
                },
            };

            loadingRef.componentInstance.data.progress = 100;
            loadingRef.close();
            loadingRef = null;

            const pdfDocGenerator = pdfMake.default.createPdf(pdfData, null, fonts, runtimeVfs);

            await new Promise<void>((resolve, reject) => {
                pdfDocGenerator.getBlob(blob => {
                    try {
                        const blobUrl = URL.createObjectURL(blob);
                        const newWindow = window.open(blobUrl, '_blank');
                        if (!newWindow) {
                            return;
                        }

                        newWindow.onload = () => {
                            try {
                                newWindow.print();
                            } catch (e) {
                                reject(new Error('Popup blocker prevented opening the new window.'));
                            }
                        };
                        resolve();

                        setTimeout(() => {
                            URL.revokeObjectURL(blobUrl);
                        }, 300000);
                    } catch (blobError) {
                        reject(blobError);
                    }
                });
            });
            return true;
        } catch (error) {
            console.error('Error generating PDF:', error);
            throw error;
        } finally {
            if (loadingRef) {
                loadingRef.close();
            }
        }
    }

    formatValue = (enable: boolean, value: number | string | boolean) => {
        if (enable) {
            if (isBoolean(value)) {
                return 'o';
            } else {
                return value;
            }
        } else return '';
    };

    formatSetPoint = (enable: boolean, setPoint: number, _fixedDigit: number, scale?: number) => {
        if (!enable || setPoint === undefined || setPoint === null) {
            return '';
        }
        const scale_val = scale || 1;
        const decimalPlaces = scale_val.toString().length - 1;
        const scaledValue = setPoint / Math.pow(10, decimalPlaces);
        const result = scaledValue.toFixed(decimalPlaces);
        return result;
    };

    formatAlarmLevelValue = (enable: boolean, level: AlarmLevel) => {
        if (!enable) return '';

        switch (level) {
            case AlarmLevel.Caution:
                return this.translate.instant('print.format.alarm.level.caution');
            case AlarmLevel.Warning:
                return this.translate.instant('print.format.alarm.level.warning');
            case AlarmLevel.Alarm:
                return this.translate.instant('print.format.alarm.level.alarm');
            default:
                return '';
        }
    };

    tableWidths = [
        40,
        195,
        114,
        38,
        'auto',
        15,
        20,
        'auto',
        'auto',
        38,
        25,
        15,
        20,
        'auto',
        'auto',
        20,
        'auto',
        'auto',
        15,
        'auto',
        60,
        15,
        '*',
    ];

    onPrintChannels: Subject<void> = new Subject<void>();

    getChannelGroups(projectId: string): Promise<ChannelGroup[]> {
        return lastValueFrom(
            this.http.get<ChannelGroup[]>(`${this.domain}/api/channel-groups`, {
                params: {
                    projectId,
                },
            })
        );
    }

    async getChannelsDifference(vesselId, iApiVesselId, iApiShipyardId, version) {
        return await this.channelCompareService.getChannelDiff(vesselId, iApiVesselId, iApiShipyardId, version);
    }

    async getCurrentChannels(vesselId, groups) {
        let channelsToPrint = [];

        if (groups.length == 0) return channelsToPrint;

        channelsToPrint = await Promise.all(
            groups.map(async group => {
                const groupId = group.id;
                return this.channelService.getChannelsByGroup(vesselId, groupId);
            })
        );
        return channelsToPrint;
    }

    getPdfFile(currentChannels: Channel[][], versionDifferences, selectedGroups: ChannelGroup[], oldVessel: any) {
        let channelsDifferences = {};
        let groupsDifferences = {};
        let updatedChannels: UpdatedChannels = { newChannels: [], deletedChannelRowsByGroup: {} };

        if (versionDifferences) {
            if (versionDifferences.channels) {
                channelsDifferences = prepareChannelDifferences(versionDifferences.channels);
                updatedChannels.newChannels = getNewChannels(versionDifferences.channels);
                updatedChannels.deletedChannelRowsByGroup = getDeletedChannels(versionDifferences.channels);
            }
            if (versionDifferences.groups) {
                groupsDifferences = prepareGroupDiffrences(versionDifferences.groups);
            }
        }

        const tableGroupHeaders = getGroupHeaders(selectedGroups, groupsDifferences);
        const isUpdatePrint = !!versionDifferences;

        return this.preparePdf(
            currentChannels,
            channelsDifferences,
            tableGroupHeaders,
            updatedChannels,
            selectedGroups,
            isUpdatePrint ? oldVessel?.drawingId : undefined
        );
    }

    generatePdfContent(
        channelsToPrint: Channel[][],
        differences,
        updatedChannels: UpdatedChannels,
        selectedGroups: any[]
    ) {
        const content = [];
        for (var i = 0; i < channelsToPrint.length; i++) {
            const currentChannels = channelsToPrint[i];
            const groupId = currentChannels[0]?.groupId;
            let deletedRowsInChannelGroup;
            if (channelsToPrint[i].length == 0) {
                deletedRowsInChannelGroup = [];
            } else {
                deletedRowsInChannelGroup = updatedChannels.deletedChannelRowsByGroup[groupId];
            }

            const matchedGroup = selectedGroups?.find(g => g.id === groupId);
            const groupRowNum = matchedGroup?.rowNum || i + 1;

            let orderedArray = this.generateTableBody(currentChannels, differences);
            let tableHeader = this.getTableHeader();
            let contentObject: any = {
                table: '',
                layout: getPdfLayout(updatedChannels, deletedRowsInChannelGroup),
                groupRowNum: groupRowNum,
                groupIndex: i,
            };

            if (i < channelsToPrint.length - 1) {
                contentObject.pageBreak = 'after';
            }

            let table = {
                headerRows: 1,
                widths: this.tableWidths,
                heights: function (row) {
                    return 22.3;
                },
                margin: [0, 0],
                dontBreakRows: true,
                body: [tableHeader, ...orderedArray],
            };

            if (table.body.length <= 1) {
                if (i > 0) {
                    const prev = content[content.length - 1];
                    if (prev && prev.pageBreak === 'after') {
                        delete prev.pageBreak;
                    }
                }
                continue;
            }

            contentObject.table = table;

            content.push(contentObject);
        }
        return content;
    }

    generateTableBody(channels: Channel[], differences) {
        channels.sort((a, b) => a.rowIndex - b.rowIndex);
        if (channels.length == 0) {
            return [];
        }
        const maxPosition = channels.length;
        const orderedArray = new Array(maxPosition).fill(new Array(23).fill(''));
        channels.map(async (channel: Channel, index) => {
            const channelRow = [];
            let channelDifferences = differences[channel.id];
            channelRow.push(this.getColumn1(channel, channelDifferences));
            channelRow.push(this.getColumn2(channel, channelDifferences));
            channelRow.push(this.getColumn3(channel, channelDifferences));
            channelRow.push(this.getColumn4(channel, channelDifferences));
            channelRow.push(this.getColumn5(channel, channelDifferences));
            channelRow.push(this.getColumn6(channel, channelDifferences));
            channelRow.push(this.getColumn7(channel, channelDifferences));
            channelRow.push(this.getColumn8(channel, channelDifferences));
            channelRow.push(this.getColumn9(channel, channelDifferences));
            channelRow.push(this.getColumn10(channel, channelDifferences));
            channelRow.push(this.getColumn11(channel, channelDifferences));
            channelRow.push(this.getColumn12(channel, channelDifferences));
            channelRow.push(this.getColumn13(channel, channelDifferences));
            channelRow.push(this.getColumn14(channel, channelDifferences));
            channelRow.push(this.getColumn15(channel, channelDifferences));
            channelRow.push(this.getColumn16(channel, channelDifferences));
            channelRow.push(this.getColumn17(channel, channelDifferences));
            channelRow.push(this.getColumn18(channel, channelDifferences));
            channelRow.push(this.getColumn19(channel, channelDifferences));
            channelRow.push(this.getColumn20(channel, channelDifferences));
            channelRow.push(this.getColumn21(channel, channelDifferences));
            channelRow.push(this.getColumn22(channel, channelDifferences));
            channelRow.push(this.getColumn23(channel, channelDifferences));

            const isRowAddedOrDeleted =
                channelDifferences?.operationType === OperationType.ADD ||
                channelDifferences?.operationType === OperationType.DELETE;

            if (isRowAddedOrDeleted) {
                channelRow.forEach((cell, i) => {
                    const borderColor =
                        i === 0
                            ? ['red', 'red', 'black', 'red']
                            : i === channelRow.length - 1
                              ? ['black', 'red', 'red', 'red']
                              : ['black', 'red', 'black', 'red'];

                    channelRow[i] =
                        typeof cell === 'object'
                            ? { ...cell, borderColor, border: [true, true, true, true] }
                            : { text: cell, borderColor, border: [true, true, true, true] };
                });
            }

            orderedArray[index] = channelRow;
        });

        const remainder = orderedArray.length % PAGE_SIZE;
        if (remainder !== 0) {
            const emptyRow = new Array(orderedArray[0].length).fill('');
            for (let i = 0; i < PAGE_SIZE - remainder; i++) {
                orderedArray.push(emptyRow);
            }
        }
        return orderedArray;
    }

    preparePdf(
        currentChannels: Channel[][],
        differences,
        tableGroupHeaders,
        updatedChannels: UpdatedChannels,
        selectedGroups: any[],
        oldDrawingId?: string
    ) {
        const lastPages = pagesPerChannelGroup(currentChannels);
        const vessel = this.vesselService.getActiveVessel();
        const DrawNo = vessel.drawingId || '';
        let getPageHeader = getHeader;
        const pdfContent = this.generatePdfContent(currentChannels, differences, updatedChannels, selectedGroups);

        const getGroupInfoByPage = (currentPage: number) => {
            for (let i = 0; i < lastPages.length; i++) {
                if (currentPage <= lastPages[i]) {
                    const contentObj = pdfContent[i];
                    return {
                        groupNo: contentObj?.groupRowNum || i + 1,
                        groupIndex: i,
                    };
                }
            }
            const lastObj = pdfContent[pdfContent.length - 1];
            return {
                groupNo: lastObj?.groupRowNum || lastPages.length,
                groupIndex: lastPages.length - 1,
            };
        };

        return {
            pageOrientation: 'landscape',
            pageSize: 'A4',
            info: {
                title: `Channels-${vessel.name}`,
            },
            header: function (currentPage) {
                const header = getPageHeader(currentPage, lastPages, tableGroupHeaders);
                return [header];
            },
            footer: function (currentPage, pageCount) {
                const info = getGroupInfoByPage(currentPage);
                let groupPageNumber = currentPage;
                if (lastPages && info.groupIndex > 0) {
                    const prevLastPage = lastPages[info.groupIndex - 1];
                    groupPageNumber = currentPage - prevLastPage;
                }

                return {
                    columns: getFooter(
                        vessel,
                        currentPage,
                        info.groupNo,
                        oldDrawingId,
                        DrawNo,
                        lastPages,
                        groupPageNumber
                    ),
                    margin: [14.1465, 0],
                    lineHeight: 0.8,
                };
            },
            content: pdfContent,

            pageMargins: [14.1465, 73.6, 14.1465, 28.3465],
            defaultStyle: {
                font: 'MSGothic',
                fontSize: 6.3,
                color: '#393939',
            },
            layout: {
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0,
            },
        };
    }

    getTableHeader() {
        return [
            { text: 'Ch No.', margin: this.DEFAULT_MARGIN },
            { text: 'Name', margin: this.DEFAULT_MARGIN },
            {
                stack: [
                    {
                        columns: [{ text: 'Status(HH/H)', width: '*' }],
                    },
                    {
                        columns: [{ text: 'Status(L/LL)', width: '*' }],
                    },
                    {
                        columns: [
                            { text: 'Range', width: '*' },
                            { text: 'Unit', width: 'auto', alignment: 'right' },
                        ],
                    },
                ],
                margin: this.DEFAULT_MARGIN,
                noWrap: true,
            },
            { stack: ['ALM H', 'ALM L', 'FEEDBACK'], margin: this.DEFAULT_MARGIN, noWrap: true },
            { stack: ['ALM', 'LEVEL'], margin: this.DEFAULT_MARGIN, noWrap: true },
            { text: 'EX', margin: this.DEFAULT_MARGIN },
            { text: 'DLY', margin: this.DEFAULT_MARGIN },
            { text: 'R1', margin: this.DEFAULT_MARGIN },
            { text: 'R2', margin: this.DEFAULT_MARGIN },
            { stack: ['ALM HH', 'ALM LL'], margin: this.DEFAULT_MARGIN, noWrap: true },
            { stack: ['ALM', 'LEVEL'], margin: this.DEFAULT_MARGIN, noWrap: true },
            { text: 'EX', margin: this.DEFAULT_MARGIN },
            { text: 'DLY', margin: this.DEFAULT_MARGIN },
            { text: 'R1', margin: this.DEFAULT_MARGIN },
            { text: 'R2', margin: this.DEFAULT_MARGIN },
            { stack: ['SEN', 'EX', 'DLY'], margin: this.DEFAULT_MARGIN },
            { text: 'R1', margin: this.DEFAULT_MARGIN },
            { text: 'R2', margin: this.DEFAULT_MARGIN },
            { text: 'IN', margin: this.DEFAULT_MARGIN },
            { text: 'S', margin: this.DEFAULT_MARGIN },
            { stack: ['FCU No.', 'FU ADDRESS'], margin: this.DEFAULT_MARGIN },
            { stack: ['AL', 'RL'], margin: this.DEFAULT_MARGIN },
            { text: 'Remarks', margin: this.DEFAULT_MARGIN },
        ];
    }

    getColumn1(channel: Channel, channelDifferences) {
        const isRowDifferences =
            channelDifferences?.operationType === OperationType.ADD ||
            channelDifferences?.operationType === OperationType.DELETE;
        const isChanged = !!channel.opsData.tag && channelDifferences?.modifiedFields?.includes('opsData.tag');

        return {
            text: channel.opsData.tag,
            margin: [1, 1, 1, 1],
            color: isChanged ? 'red' : isRowDifferences ? 'red' : 'black',
            noWrap: true,
            borderColor: isChanged ? ['red', 'red', 'red', 'red'] : [],
            borderWidth: isChanged ? [2, 2, 2, 2] : [],
        };
    }

    getColumn2(channel: Channel, channelDifferences) {
        const isRowDifferences =
            channelDifferences?.operationType === OperationType.ADD ||
            channelDifferences?.operationType === OperationType.DELETE;
        const isChanged =
            channelDifferences?.oldName &&
            channelDifferences?.newName &&
            channelDifferences.oldName !== channelDifferences.newName;

        return {
            text: channel.opsData.name,
            margin: [1, 1, 1, 1],
            color: isChanged || isRowDifferences ? 'red' : undefined,
            borderColor: isChanged ? ['red', 'red', 'red', 'red'] : [],
            borderWidth: isChanged ? [2, 2, 2, 2] : [],
        };
    }

    getColumn3(channel: Channel, channelDifferences) {
        if (channel.opsData.channelType.includes('Digital')) {
            if (channel.opsData.channelType == ChannelTypeStrings[ChannelType.DIGITAL_MOTOR]) {
                return generateInnerTable(channelDifferences, [
                    {
                        text: channel.fcuData.motorChannelSetting.motorType || '',
                        fields: ['fcuData.motorChannelSetting.motorType'],
                    },
                ]);
            } else
                return generateInnerTable(channelDifferences, [
                    {
                        text:
                            channel.fcuData.digitalAlarmSetting.statusStringContactOn +
                                '/' +
                                channel.fcuData.digitalAlarmSetting.statusStringContactOff || '',
                        fields: [
                            'fcuData.digitalAlarmSetting.statusStringContactOn',
                            'fcuData.digitalAlarmSetting.statusStringContactOff',
                        ],
                    },
                ]);
        } else if (channel.opsData.channelType == ChannelTypeStrings[ChannelType.BLANK]) {
            return '';
        } else {
            const statusString = this.getChannelStatusString(channel);
            const min = channel?.fcuData?.measurableRange?.min ?? null;
            const max = channel?.fcuData?.measurableRange?.max ?? null;
            const scale = channel?.fcuData?.scale ?? 1;

            function formatNumberForPrint(value: number): string {
                if (value == null) return '';
                const scaled = value / scale;
                const decimals = scale === 1 ? 0 : Math.max(Math.log10(scale), 0);
                return scaled.toFixed(decimals);
            }

            const rangeText =
                min != null && max != null ? `${formatNumberForPrint(min)} / ${formatNumberForPrint(max)}` : 'N/A';

            const statusRows = generateInnerTable(channelDifferences, [
                {
                    text: statusString.highStatus,
                    fields: ['fcuData.alarmSettingVeryHigh.enable', 'fcuData.alarmSettingHigh.enable'],
                },
                {
                    text: statusString.lowStatus,
                    fields: ['fcuData.alarmSettingLow.enable', 'fcuData.alarmSettingVeryLow.enable'],
                },
            ]);

            statusRows.table.body.forEach(row => {
                row.forEach(cell => {
                    (cell as any).fontSize = 5.8;
                });
            });

            const rangeUnitRow = {
                table: {
                    widths: ['*', 'auto'],
                    body: [
                        [
                            {
                                text: rangeText,
                                margin: this.DEFAULT_MARGIN,
                                noWrap: true,
                            },
                            {
                                text: channel?.opsData?.measurementUnit || '',
                                margin: this.DEFAULT_MARGIN,
                                alignment: 'right',
                                noWrap: true,
                            },
                        ],
                    ],
                },
                layout: 'noBorders',
                margin: this.DEFAULT_MARGIN,
            };

            return {
                stack: [statusRows, rangeUnitRow],
            };
        }
    }

    getChannelStatusString(channel: Channel) {
        const channelType = channel.fcuData.channelType;

        const hasHHSetting =
            isAnalog(channelType) ||
            isRunningHour(channelType) ||
            isFlowCounter(channelType) ||
            isExhGasDev(channelType);
        const hasHSetting =
            isAnalog(channelType) ||
            isRunningHour(channelType) ||
            isFlowCounter(channelType) ||
            isExhGasDev(channelType);
        const hasLSetting = isAnalog(channelType) || isExhaustGas(channelType);
        const hasLLSetting = isAnalog(channelType) || isExhGasDev(channelType);

        const highStatus = [
            hasHHSetting && channel?.fcuData?.alarmSettingVeryHigh?.enable
                ? channel.fcuData.alarmSettingVeryHigh.statusString || ''
                : '',
            hasHSetting && channel?.fcuData?.alarmSettingHigh?.enable
                ? channel.fcuData.alarmSettingHigh.statusString || ''
                : '',
        ]
            .filter(s => s)
            .join(' / ');

        const lowStatus = [
            hasLSetting && channel?.fcuData?.alarmSettingLow?.enable
                ? channel.fcuData.alarmSettingLow.statusString || ''
                : '',
            hasLLSetting && channel?.fcuData?.alarmSettingVeryLow?.enable
                ? channel.fcuData.alarmSettingVeryLow.statusString || ''
                : '',
        ]
            .filter(s => s)
            .join(' / ');

        return { highStatus, lowStatus };
    }

    getColumn4(channel: Channel, channelDifferences) {
        const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.alarmSettingHigh.enable');
        const forceRedMiddle = isEnableToggled(channelDifferences, 'fcuData.alarmSettingLow.enable');
        const scale = channel?.fcuData?.scale || 1;
        const channelType = channel.fcuData.channelType;
        const hasHSetting =
            isAnalog(channelType) ||
            isRunningHour(channelType) ||
            isFlowCounter(channelType) ||
            isExhGasDev(channelType);
        const hasLSetting = isAnalog(channelType) || isExhaustGas(channelType);
        const hasFESetting = isDigitalMotor(channelType);

        return generateInnerTable(
            channelDifferences,
            [
                {
                    text: this.formatSetPoint(
                        hasHSetting && channel?.fcuData?.alarmSettingHigh?.enable,
                        channel?.fcuData?.alarmSettingHigh?.setPoint,
                        channel?.fcuData?.fixedDigit,
                        scale
                    ),
                    fields: ['fcuData.alarmSettingHigh.setPoint'],
                },
                {
                    text: this.formatSetPoint(
                        hasLSetting && channel?.fcuData?.alarmSettingLow?.enable,
                        channel?.fcuData?.alarmSettingLow?.setPoint,
                        channel?.fcuData?.fixedDigit,
                        scale
                    ),
                    fields: ['fcuData.alarmSettingLow.setPoint'],
                },
                {
                    text: this.formatSetPoint(
                        hasFESetting && channel?.fcuData?.alarmSettingFeedbackError?.enable,
                        channel?.fcuData?.alarmSettingFeedbackError?.setPoint,
                        channel?.fcuData?.fixedDigit,
                        scale
                    ),
                    fields: ['fcuData.alarmSettingFeedbackError.setPoint'],
                },
            ],
            true,
            false,
            forceRedTop,
            forceRedMiddle
        );
    }

    getColumn5(channel: Channel, channelDifferences) {
        const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.alarmSettingHigh.enable');
        const forceRedMiddle = isEnableToggled(channelDifferences, 'fcuData.alarmSettingLow.enable');
        const channelType = channel.fcuData.channelType;
        if (channel.opsData.channelType.includes('Digital')) {
            if (channel.opsData.channelType == ChannelTypeStrings[ChannelType.DIGITAL_MOTOR]) {
                const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.motorChannelSetting.enable');
                return generateInnerTable(
                    channelDifferences,
                    [
                        {
                            text: this.formatAlarmLevelValue(
                                channel.fcuData.motorChannelSetting.enable,
                                channel.fcuData.motorChannelSetting.alarmLevel
                            ),
                            fields: ['fcuData.motorChannelSetting.alarmLevel'],
                        },
                        { text: '', fields: [] },
                        {
                            text: this.formatAlarmLevelValue(
                                channel?.fcuData?.alarmSettingFeedbackError?.enable ?? false,
                                channel?.fcuData?.alarmSettingFeedbackError?.alarmLevel
                            ),
                            fields: ['fcuData.alarmSettingFeedbackError.alarmLevel'],
                        },
                    ],
                    false,
                    false,
                    forceRedTop
                );
            } else {
                return generateInnerTable(
                    channelDifferences,
                    [
                        {
                            text: this.formatAlarmLevelValue(
                                channel.fcuData.digitalAlarmSetting.enable,
                                channel.fcuData.digitalAlarmSetting.alarmLevel
                            ),
                            fields: ['fcuData.digitalAlarmSetting.alarmLevel'],
                        },
                    ],
                    false,
                    false,
                    forceRedTop,
                    forceRedMiddle
                );
            }
        } else {
            const hasHSetting =
                isAnalog(channelType) ||
                isRunningHour(channelType) ||
                isFlowCounter(channelType) ||
                isExhGasDev(channelType);
            const hasLSetting = isAnalog(channelType) || isExhaustGas(channelType);
            return generateInnerTable(
                channelDifferences,
                [
                    {
                        text: this.formatAlarmLevelValue(
                            hasHSetting && channel?.fcuData?.alarmSettingHigh?.enable,
                            channel?.fcuData?.alarmSettingHigh?.alarmLevel
                        ),
                        fields: ['fcuData.alarmSettingHigh.alarmLevel'],
                    },
                    {
                        text: this.formatAlarmLevelValue(
                            hasLSetting && channel?.fcuData?.alarmSettingLow?.enable,
                            channel?.fcuData?.alarmSettingLow?.alarmLevel
                        ),
                        fields: ['fcuData.alarmSettingLow.alarmLevel'],
                    },
                ],
                false,
                false,
                forceRedTop,
                forceRedMiddle
            );
        }
    }

    getColumn6(channel: Channel, channelDifferences) {
        const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.alarmSettingHigh.enable');
        const forceRedMiddle = isEnableToggled(channelDifferences, 'fcuData.alarmSettingLow.enable');
        const channelType = channel.fcuData.channelType;
        if (channel.opsData.channelType.includes('Digital')) {
            const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.motorChannelSetting.enable');
            if (channel.opsData.channelType == ChannelTypeStrings[ChannelType.DIGITAL_MOTOR]) {
                return generateInnerTable(
                    channelDifferences,
                    [
                        {
                            text: this.formatValue(
                                channel.fcuData.motorChannelSetting.enable,
                                channel.fcuData.motorChannelSetting.alarmGroupNo
                            ),
                            fields: ['fcuData.motorChannelSetting.alarmGroupNo'],
                        },
                        { text: '', fields: [] },
                        {
                            text: this.formatValue(
                                channel?.fcuData?.alarmSettingFeedbackError?.enable ?? false,
                                channel?.fcuData?.alarmSettingFeedbackError?.alarmGroupNo
                            ),
                            fields: ['fcuData.alarmSettingFeedbackError.alarmGroupNo'],
                        },
                    ],
                    false,
                    false,
                    forceRedTop
                );
            } else {
                if (channel.fcuData.digitalAlarmSetting.enable) {
                    return generateInnerTable(
                        channelDifferences,
                        [
                            {
                                text: channel.fcuData.digitalAlarmSetting.alarmGroupNo,
                                fields: ['fcuData.digitalAlarmSetting.alarmGroupNo'],
                            },
                        ],
                        false,
                        false,
                        forceRedTop,
                        forceRedMiddle
                    );
                }
            }
        } else {
            const hasHSetting =
                isAnalog(channelType) ||
                isRunningHour(channelType) ||
                isFlowCounter(channelType) ||
                isExhGasDev(channelType);
            const hasLSetting = isAnalog(channelType) || isExhaustGas(channelType);
            return generateInnerTable(
                channelDifferences,
                [
                    {
                        text: this.formatValue(
                            hasHSetting && channel?.fcuData?.alarmSettingHigh?.enable,
                            channel?.fcuData?.alarmSettingHigh?.alarmGroupNo
                        ),
                        fields: ['fcuData.alarmSettingHigh.alarmGroupNo'],
                    },
                    {
                        text: this.formatValue(
                            hasLSetting && channel?.fcuData?.alarmSettingLow?.enable,
                            channel?.fcuData?.alarmSettingLow?.alarmGroupNo
                        ),
                        fields: ['fcuData.alarmSettingLow.alarmGroupNo'],
                    },
                ],
                false,
                false,
                forceRedTop,
                forceRedMiddle
            );
        }
        return generateInnerTable(
            channelDifferences,
            [
                {
                    text: '',
                    fields: [],
                },
            ],
            false,
            false,
            forceRedTop,
            forceRedMiddle
        );
    }

    getColumn7(channel: Channel, channelDifferences) {
        const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.alarmSettingHigh.enable');
        const forceRedMiddle = isEnableToggled(channelDifferences, 'fcuData.alarmSettingLow.enable');
        const channelType = channel.fcuData.channelType;
        if (channel?.opsData?.channelType?.includes('Digital')) {
            const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.motorChannelSetting.enable');
            if (channel?.opsData.channelType === ChannelTypeStrings[ChannelType.DIGITAL_MOTOR]) {
                return generateInnerTable(
                    channelDifferences,
                    [
                        {
                            text: this.formatValue(
                                channel?.fcuData?.motorChannelSetting?.enable ?? false,
                                channel?.fcuData?.motorChannelSetting?.delayTimer
                            ),
                            fields: ['fcuData.motorChannelSetting.delayTimer'],
                        },
                        { text: '', fields: [''] },
                        {
                            text: this.formatValue(
                                channel?.fcuData?.alarmSettingFeedbackError?.enable ?? false,
                                channel?.fcuData?.alarmSettingFeedbackError?.delayTimer
                            ),
                            fields: ['fcuData.alarmSettingFeedbackError.delayTimer'],
                        },
                    ],
                    false,
                    false,
                    forceRedTop
                );
            } else {
                if (channel.fcuData.digitalAlarmSetting.enable) {
                    return generateInnerTable(
                        channelDifferences,
                        [
                            {
                                text: channel?.fcuData?.digitalAlarmSetting?.delayTimer,
                                fields: ['fcuData.digitalAlarmSetting.delayTimer'],
                            },
                        ],
                        false,
                        false,
                        forceRedTop,
                        forceRedMiddle
                    );
                }
            }
        } else {
            const hasHSetting =
                isAnalog(channelType) ||
                isRunningHour(channelType) ||
                isFlowCounter(channelType) ||
                isExhGasDev(channelType);
            const hasLSetting = isAnalog(channelType) || isExhaustGas(channelType);

            return generateInnerTable(
                channelDifferences,
                [
                    {
                        text: this.formatValue(
                            hasHSetting && channel?.fcuData?.alarmSettingHigh?.enable,
                            channel?.fcuData?.alarmSettingHigh?.delayTimer
                        ),
                        fields: ['fcuData.alarmSettingHigh.delayTimer'],
                    },
                    {
                        text: this.formatValue(
                            hasLSetting && channel?.fcuData?.alarmSettingLow?.enable,
                            channel?.fcuData?.alarmSettingLow?.delayTimer
                        ),
                        fields: ['fcuData.alarmSettingLow.delayTimer'],
                    },
                ],
                false,
                false,
                forceRedTop,
                forceRedMiddle
            );
        }
        return generateInnerTable(
            channelDifferences,
            [
                {
                    text: '',
                    fields: [],
                },
            ],
            false,
            false,
            forceRedTop,
            forceRedMiddle
        );
    }

    getColumn8(channel: Channel, channelDifferences) {
        const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.alarmSettingHigh.enable');
        const forceRedMiddle = isEnableToggled(channelDifferences, 'fcuData.alarmSettingLow.enable');
        const channelType = channel.fcuData.channelType;
        if (channel?.opsData?.channelType?.includes('Digital')) {
            const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.motorChannelSetting.enable');
            if (channel?.opsData.channelType === ChannelTypeStrings[ChannelType.DIGITAL_MOTOR]) {
                return generateInnerTable(
                    channelDifferences,
                    [
                        {
                            text: this.formatValue(
                                channel?.fcuData?.motorChannelSetting?.enable ?? false,
                                channel?.fcuData?.motorChannelSetting?.reposeGroup1No
                            ),
                            fields: ['fcuData.motorChannelSetting.reposeGroup1No'],
                        },
                        { text: '', fields: [''] },
                        {
                            text: this.formatValue(
                                channel?.fcuData?.alarmSettingFeedbackError?.enable ?? false,
                                channel?.fcuData?.alarmSettingFeedbackError?.reposeGroup1No
                            ),
                            fields: ['fcuData.alarmSettingFeedbackError.reposeGroup1No'],
                        },
                    ],
                    false,
                    false,
                    forceRedTop
                );
            } else {
                if (channel.fcuData.digitalAlarmSetting.enable) {
                    return generateInnerTable(
                        channelDifferences,
                        [
                            {
                                text: channel?.fcuData?.digitalAlarmSetting?.reposeGroup1No,
                                fields: ['fcuData.digitalAlarmSetting.reposeGroup1No'],
                            },
                        ],
                        false,
                        false,
                        forceRedTop
                    );
                }
            }
        } else {
            const hasHSetting =
                isAnalog(channelType) ||
                isRunningHour(channelType) ||
                isFlowCounter(channelType) ||
                isExhGasDev(channelType);
            const hasLSetting = isAnalog(channelType) || isExhaustGas(channelType);

            return generateInnerTable(
                channelDifferences,
                [
                    {
                        text: this.formatValue(
                            hasHSetting && channel?.fcuData?.alarmSettingHigh?.enable,
                            channel?.fcuData?.alarmSettingHigh?.reposeGroup1No
                        ),
                        fields: ['fcuData.alarmSettingHigh.reposeGroup1No'],
                    },
                    {
                        text: this.formatValue(
                            hasLSetting && channel?.fcuData?.alarmSettingLow?.enable,
                            channel?.fcuData?.alarmSettingLow?.reposeGroup1No
                        ),
                        fields: ['fcuData.alarmSettingLow.reposeGroup1No'],
                    },
                ],
                false,
                false,
                forceRedTop,
                forceRedMiddle
            );
        }
        return generateInnerTable(
            channelDifferences,
            [
                {
                    text: '',
                    fields: [],
                },
            ],
            false,
            false,
            forceRedTop,
            forceRedMiddle
        );
    }

    getColumn9(channel: Channel, channelDifferences) {
        const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.alarmSettingHigh.enable');
        const forceRedMiddle = isEnableToggled(channelDifferences, 'fcuData.alarmSettingLow.enable');
        const channelType = channel.fcuData.channelType;
        if (channel?.opsData?.channelType?.includes('Digital')) {
            const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.motorChannelSetting.enable');
            if (channel?.opsData.channelType === ChannelTypeStrings[ChannelType.DIGITAL_MOTOR]) {
                return generateInnerTable(
                    channelDifferences,
                    [
                        {
                            text: this.formatValue(
                                channel?.fcuData?.motorChannelSetting?.enable ?? false,
                                channel?.fcuData?.motorChannelSetting?.reposeGroup2No
                            ),
                            fields: ['fcuData.motorChannelSetting.reposeGroup2No'],
                        },
                        { text: '', fields: [''] },
                        {
                            text: this.formatValue(
                                channel?.fcuData?.alarmSettingFeedbackError?.enable ?? false,
                                channel?.fcuData?.alarmSettingFeedbackError?.reposeGroup2No
                            ),
                            fields: ['fcuData.alarmSettingFeedbackError.reposeGroup2No'],
                        },
                    ],
                    false,
                    true,
                    forceRedTop
                );
            } else {
                if (channel.fcuData.digitalAlarmSetting.enable) {
                    return generateInnerTable(
                        channelDifferences,
                        [
                            {
                                text: channel?.fcuData?.digitalAlarmSetting?.reposeGroup2No,
                                fields: ['fcuData.digitalAlarmSetting.reposeGroup2No'],
                            },
                        ],
                        false,
                        true,
                        forceRedTop
                    );
                }
            }
        } else {
            const hasHSetting =
                isAnalog(channelType) ||
                isRunningHour(channelType) ||
                isFlowCounter(channelType) ||
                isExhGasDev(channelType);
            const hasLSetting = isAnalog(channelType) || isExhaustGas(channelType);
            return generateInnerTable(
                channelDifferences,
                [
                    {
                        text: this.formatValue(
                            hasHSetting && channel?.fcuData?.alarmSettingHigh?.enable,
                            channel?.fcuData?.alarmSettingHigh?.reposeGroup2No
                        ),
                        fields: ['fcuData.alarmSettingHigh.reposeGroup2No'],
                    },
                    {
                        text: this.formatValue(
                            hasLSetting && channel?.fcuData?.alarmSettingLow?.enable,
                            channel?.fcuData?.alarmSettingLow?.reposeGroup2No
                        ),
                        fields: ['fcuData.alarmSettingLow.reposeGroup2No'],
                    },
                ],
                false,
                true,
                forceRedTop,
                forceRedMiddle
            );
        }
        return generateInnerTable(
            channelDifferences,
            [
                {
                    text: '',
                    fields: [],
                },
            ],
            false,
            true,
            forceRedTop,
            forceRedMiddle
        );
    }

    getColumn10(channel: Channel, channelDifferences) {
        const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.alarmSettingVeryHigh.enable');
        const forceRedMiddle = isEnableToggled(channelDifferences, 'fcuData.alarmSettingVeryLow.enable');
        const scale = channel?.fcuData?.scale || 1;
        const channelType = channel.fcuData.channelType;
        const hasHHSetting =
            isAnalog(channelType) ||
            isRunningHour(channelType) ||
            isFlowCounter(channelType) ||
            isExhGasDev(channelType);
        const hasLLSetting = isAnalog(channelType) || isExhGasDev(channelType);

        return generateInnerTable(
            channelDifferences,
            [
                {
                    text: this.formatSetPoint(
                        hasHHSetting && channel?.fcuData?.alarmSettingVeryHigh?.enable,
                        channel?.fcuData?.alarmSettingVeryHigh?.setPoint,
                        channel?.fcuData?.fixedDigit,
                        scale
                    ),
                    fields: ['fcuData.alarmSettingVeryHigh.setPoint'],
                },
                {
                    text: this.formatSetPoint(
                        hasLLSetting && channel?.fcuData?.alarmSettingVeryLow?.enable,
                        channel?.fcuData?.alarmSettingVeryLow?.setPoint,
                        channel?.fcuData?.fixedDigit,
                        scale
                    ),
                    fields: ['fcuData.alarmSettingVeryLow.setPoint'],
                },
            ],
            true,
            false,
            forceRedTop,
            forceRedMiddle
        );
    }

    getColumn11(channel: Channel, channelDifferences) {
        const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.alarmSettingVeryHigh.enable');
        const forceRedMiddle = isEnableToggled(channelDifferences, 'fcuData.alarmSettingVeryLow.enable');
        const channelType = channel.fcuData.channelType;
        const hasHHSetting =
            isAnalog(channelType) ||
            isRunningHour(channelType) ||
            isFlowCounter(channelType) ||
            isExhGasDev(channelType);
        const hasLLSetting = isAnalog(channelType) || isExhGasDev(channelType);
        return generateInnerTable(
            channelDifferences,
            [
                {
                    text: this.formatAlarmLevelValue(
                        hasHHSetting && channel?.fcuData?.alarmSettingVeryHigh?.enable,
                        channel?.fcuData?.alarmSettingVeryHigh?.alarmLevel
                    ),
                    fields: ['fcuData.alarmSettingVeryHigh.alarmLevel'],
                },
                {
                    text: this.formatAlarmLevelValue(
                        hasLLSetting && channel?.fcuData?.alarmSettingVeryLow?.enable,
                        channel?.fcuData?.alarmSettingVeryLow?.alarmLevel
                    ),
                    fields: ['fcuData.alarmSettingVeryLow.alarmLevel'],
                },
            ],
            false,
            false,
            forceRedTop,
            forceRedMiddle
        );
    }

    getColumn12(channel: Channel, channelDifferences) {
        const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.alarmSettingVeryHigh.enable');
        const forceRedMiddle = isEnableToggled(channelDifferences, 'fcuData.alarmSettingVeryLow.enable');
        const channelType = channel.fcuData.channelType;
        const hasHHSetting =
            isAnalog(channelType) ||
            isRunningHour(channelType) ||
            isFlowCounter(channelType) ||
            isExhGasDev(channelType);
        const hasLLSetting = isAnalog(channelType) || isExhGasDev(channelType);
        return generateInnerTable(
            channelDifferences,
            [
                {
                    text: this.formatValue(
                        hasHHSetting && channel?.fcuData?.alarmSettingVeryHigh?.enable,
                        channel?.fcuData?.alarmSettingVeryHigh?.alarmGroupNo
                    ),
                    fields: ['fcuData.alarmSettingVeryHigh.alarmGroupNo'],
                },
                {
                    text: this.formatValue(
                        hasLLSetting && channel?.fcuData?.alarmSettingVeryLow?.enable,
                        channel?.fcuData?.alarmSettingVeryLow?.alarmGroupNo
                    ),
                    fields: ['fcuData.alarmSettingVeryLow.alarmGroupNo'],
                },
            ],
            false,
            false,
            forceRedTop,
            forceRedMiddle
        );
    }

    getColumn13(channel: Channel, channelDifferences) {
        const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.alarmSettingVeryHigh.enable');
        const forceRedMiddle = isEnableToggled(channelDifferences, 'fcuData.alarmSettingVeryLow.enable');
        const channelType = channel.fcuData.channelType;
        const hasHHSetting =
            isAnalog(channelType) ||
            isRunningHour(channelType) ||
            isFlowCounter(channelType) ||
            isExhGasDev(channelType);
        const hasLLSetting = isAnalog(channelType) || isExhGasDev(channelType);
        return generateInnerTable(
            channelDifferences,
            [
                {
                    text: this.formatValue(
                        hasHHSetting && channel?.fcuData?.alarmSettingVeryHigh?.enable,
                        channel?.fcuData?.alarmSettingVeryHigh?.delayTimer
                    ),
                    fields: ['fcuData.alarmSettingVeryHigh.delayTimer'],
                },
                {
                    text: this.formatValue(
                        hasLLSetting && channel?.fcuData?.alarmSettingVeryLow?.enable,
                        channel?.fcuData?.alarmSettingVeryLow?.delayTimer
                    ),
                    fields: ['fcuData.alarmSettingVeryLow.delayTimer'],
                },
            ],
            false,
            false,
            forceRedTop,
            forceRedMiddle
        );
    }

    getColumn14(channel: Channel, channelDifferences) {
        const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.alarmSettingVeryHigh.enable');
        const forceRedMiddle = isEnableToggled(channelDifferences, 'fcuData.alarmSettingVeryLow.enable');
        const channelType = channel.fcuData.channelType;
        const hasHHSetting =
            isAnalog(channelType) ||
            isRunningHour(channelType) ||
            isFlowCounter(channelType) ||
            isExhGasDev(channelType);
        const hasLLSetting = isAnalog(channelType) || isExhGasDev(channelType);
        return generateInnerTable(
            channelDifferences,
            [
                {
                    text: this.formatValue(
                        hasHHSetting && channel?.fcuData?.alarmSettingVeryHigh?.enable,
                        channel?.fcuData?.alarmSettingVeryHigh?.reposeGroup1No
                    ),
                    fields: ['fcuData.alarmSettingVeryHigh.reposeGroup1No'],
                },
                {
                    text: this.formatValue(
                        hasLLSetting && channel?.fcuData?.alarmSettingVeryLow?.enable,
                        channel?.fcuData?.alarmSettingVeryLow?.reposeGroup1No
                    ),
                    fields: ['fcuData.alarmSettingVeryLow.reposeGroup1No'],
                },
            ],
            false,
            false,
            forceRedTop,
            forceRedMiddle
        );
    }

    getColumn15(channel: Channel, channelDifferences) {
        const forceRedTop = isEnableToggled(channelDifferences, 'fcuData.alarmSettingVeryHigh.enable');
        const forceRedMiddle = isEnableToggled(channelDifferences, 'fcuData.alarmSettingVeryLow.enable');
        const channelType = channel.fcuData.channelType;
        const hasHHSetting =
            isAnalog(channelType) ||
            isRunningHour(channelType) ||
            isFlowCounter(channelType) ||
            isExhGasDev(channelType);
        const hasLLSetting = isAnalog(channelType) || isExhGasDev(channelType);
        return generateInnerTable(
            channelDifferences,
            [
                {
                    text: this.formatValue(
                        hasHHSetting && channel?.fcuData?.alarmSettingVeryHigh?.enable,
                        channel?.fcuData?.alarmSettingVeryHigh?.reposeGroup2No
                    ),
                    fields: ['fcuData.alarmSettingVeryHigh.reposeGroup2No'],
                },
                {
                    text: this.formatValue(
                        hasLLSetting && channel?.fcuData?.alarmSettingVeryLow?.enable,
                        channel?.fcuData?.alarmSettingVeryLow?.reposeGroup2No
                    ),
                    fields: ['fcuData.alarmSettingVeryLow.reposeGroup2No'],
                },
            ],
            false,
            true,
            forceRedTop,
            forceRedMiddle
        );
    }

    getColumn16(channel: Channel, channelDifferences) {
        const channelType = channel.fcuData.channelType;
        const hasSFSetting = isAnalog(channelType);
        return generateInnerTable(channelDifferences, [
            {
                text: this.formatValue(hasSFSetting, channel?.fcuData?.alarmSettingSensorFail?.enable),
                fields: ['fcuData.alarmSettingSensorFail.enable'],
            },
            {
                text: this.formatValue(hasSFSetting, channel?.fcuData?.alarmSettingSensorFail?.alarmGroupNo),
                fields: ['fcuData.alarmSettingSensorFail.alarmGroupNo'],
            },
            {
                text: this.formatValue(hasSFSetting, channel?.fcuData?.alarmSettingSensorFail?.delayTimer),
                fields: ['fcuData.alarmSettingSensorFail.delayTimer'],
            },
        ]);
    }

    getColumn17(channel: Channel, channelDifferences) {
        const channelType = channel.fcuData.channelType;
        const hasSFSetting = isAnalog(channelType);
        return generateInnerTable(channelDifferences, [
            {
                text: this.formatValue(hasSFSetting, channel?.fcuData?.alarmSettingSensorFail?.reposeGroup1No),
                fields: ['fcuData.alarmSettingSensorFail.reposeGroup1No'],
            },
        ]);
    }

    getColumn18(channel: Channel, channelDifferences) {
        const channelType = channel.fcuData.channelType;
        const hasSFSetting = isAnalog(channelType);
        return generateInnerTable(channelDifferences, [
            {
                text: this.formatValue(hasSFSetting, channel?.fcuData?.alarmSettingSensorFail?.reposeGroup2No),
                fields: ['fcuData.alarmSettingSensorFail.reposeGroup2No'],
            },
        ]);
    }

    getColumn19(channel: Channel, channelDifferences) {
        let channelType = getSimpleType(channel.fcuData);
        let isRowDifferences =
            channelDifferences?.operationType === OperationType.ADD ||
            channelDifferences?.operationType === OperationType.DELETE;

        const isChanged =
            channelDifferences?.oldChannelType &&
            channelDifferences?.newChannelType &&
            channelDifferences.oldChannelType !== channelDifferences.newChannelType;

        if (isRowDifferences) {
            return {
                text: channelType,
                color: 'red',
            };
        } else {
            return {
                text: channelType,
                color: isChanged ? 'red' : 'black',
                borderColor: isChanged ? ['red', 'red', 'red', 'red'] : [],
                borderWidth: isChanged ? [2, 2, 2, 2] : [],
            };
        }
    }

    getColumn20(channel: Channel, channelDifferences) {
        const channelType = channel?.fcuData?.channelType;
        const specialChannelType = getSpecialType(channelType) ?? '';

        let isRowDifferences =
            channelDifferences?.operationType === OperationType.ADD ||
            channelDifferences?.operationType === OperationType.DELETE;

        const isChanged =
            channelDifferences?.oldChannelType &&
            channelDifferences?.newChannelType &&
            channelDifferences.oldChannelType !== channelDifferences.newChannelType;

        if (isRowDifferences) {
            return {
                text: specialChannelType,
                color: 'red',
            };
        } else {
            return {
                text: specialChannelType,
                color: isChanged ? 'red' : 'black',
                borderColor: isChanged ? ['red', 'red', 'red', 'red'] : [],
                borderWidth: isChanged ? [2, 2, 2, 2] : [],
            };
        }
    }

    getColumn21(channel: Channel, channelDifferences) {
        const fcu = channel?.fcuData;
        const input = fcu?.inputData?.fuInputAddress;

        return generateInnerTable(channelDifferences, [
            {
                text: fcu?.fcuNo,
                fields: ['fcuData.fcuNo'],
            },
            {
                text:
                    (input?.fuNo || input?.fuNo == 0) &&
                    (input?.slotNo || input?.slotNo == 0) &&
                    (input?.pin || input?.pin == 0)
                        ? 'FU ' + input.fuNo + ' - ' + input.slotNo + ' - ' + input.pin
                        : '',
                fields: [
                    'fcuData.inputData.fuInputAddress.fuNo',
                    'fcuData.inputData.fuInputAddress.slotNo',
                    'fcuData.inputData.fuInputAddress.pin',
                ],
            },
        ]);
    }

    getColumn22(channel: Channel, channelDifferences) {
        return generateInnerTable(channelDifferences, [
            {
                text: ' ',
                fields: [],
            },
            {
                text: ' ',
                fields: [],
            },
        ]);
    }

    getColumn23(channel: Channel, channelDifferences) {
        const remarks = channel.opsData.remarks;
        const remarkParts = remarks?.split('^') ?? [];
        const tableData = [];

        for (let i = 0; i < 3; i++) {
            if (i == 0) {
                if (isAnalog(channel.fcuData.channelType)) {
                    const normalMin = channel.fcuData.normalRange.min;
                    const normalMax = channel.fcuData.normalRange.max;
                    const scale = channel?.fcuData?.scale ?? 1;

                    function formatNumberForPrint(value: number): string {
                        if (value == null) return '';
                        const scaled = value / scale;
                        const decimals = scale === 1 ? 0 : Math.max(Math.log10(scale), 0);
                        return scaled.toFixed(decimals);
                    }

                    let analogData = '';
                    if (Number.isFinite(normalMax) && Number.isFinite(normalMin)) {
                        analogData = `G: ${formatNumberForPrint(normalMin)}-${formatNumberForPrint(normalMax)}`;
                    }
                    tableData.push({ text: analogData, fields: [] });
                } else tableData.push({ text: '', fields: [] });
            } else {
                const remark = remarkParts?.[i - 1] ?? '';
                tableData.push({
                    text: remark,
                    fields: [],
                });
            }
        }

        return generateInnerTable(channelDifferences, tableData);
    }
}

export enum OperationType {
    UPDATE = 'update',
    ADD = 'add',
    DELETE = 'delete',
}

export class UpdatedChannels {
    newChannels: string[];
    deletedChannelRowsByGroup: { [string: string]: number[] };
}
